import CustomCalendar from '@/components/CustomComponent/CustomCalendar/CustomCalendar';
import CustomDropdown from '@/components/CustomComponent/CustomDropdown/CustomDropdown';
import CustomMultiSelect from '@/components/CustomComponent/CustomMultiSelect/CustomMultiSelect';
import { NETWORK_SORT_OPTIONS, QUERY_STRING_STORE } from '@/helpers/constants';
import debounce from 'lodash/debounce';
import { MultiSelect } from 'primereact/multiselect';
import { ScrollTop } from 'primereact/scrolltop';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { COMMON_TEXT } from 'src/helpers/common-text';
import NetworkClientCount from './NetworkClientCount';
import NetworkPerformanceScore from './NetworkPerformanceScore';
import NetworkUsage from './NetworkUsage';
import WirelessChannelUtilization from './WirelessChannelUtilization';
import WirelessLatency from './WirelessLatency';
import WirelessSignal from './WirelessSignal';
import { fetchLineChartThresholds } from './query-request';
import './Network.less';

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class Network extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tenant:
        this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0],
      selectedDate: new Date(),
      selectedLimit: 5,
      selectedSort: NETWORK_SORT_OPTIONS.PERFORMANCE_SCORE_BAR_DESC,
      selectedLineList: [],
      lineOptions: [],
      selectedDeviceIdLineList: [],
      deviceInfoOrderedList: [],
      addedDeviceIdList: [],
      isResetDeviceIdList: false,
      lineChartThresholds: {},
      activeTab: 0, // 0: Performance Score + Signal Quality, 1: Connection/Data/Channel, 2: Latency
    };
    this.limitDropDownOptions = [
      { label: COMMON_TEXT.TOP_3, value: 3 },
      { label: COMMON_TEXT.TOP_5, value: 5 },
      { label: COMMON_TEXT.TOP_10, value: 10 },
      { label: COMMON_TEXT.TOP_ALL, value: 0 },
    ];
    this.updateDeviceInfoOrderList = this.updateDeviceInfoOrderList.bind(this);
    this.sortDropDownOptions = [
      {
        label: COMMON_TEXT.SORT_BY_DEVICE_NAME,
        value: NETWORK_SORT_OPTIONS.DEVICE_NAME_ASC,
      },
      {
        label: COMMON_TEXT.SORT_BY_PERFORMANCE_SCORE_DESC,
        value: NETWORK_SORT_OPTIONS.PERFORMANCE_SCORE_BAR_DESC,
      },
      {
        label: COMMON_TEXT.SORT_BY_PERFORMANCE_SCORE_ASC,
        value: NETWORK_SORT_OPTIONS.PERFORMANCE_SCORE_BAR_ASC,
      },
    ];
    this.loadedDeviceIdList = [];
  }

  componentDidMount() {
    const { tenant } = this.state;
    if (tenant) {
      this.getLineChartThresholds();
    }
  }

  getLineChartThresholds = async () => {
    const { tenant } = this.state;
    const res = await fetchLineChartThresholds({ tenantId: tenant });
    // group res by chart_name
    const groupByChartName = res.reduce((acc, item) => {
      const { chart_name } = item;
      if (!acc[chart_name]) {
        acc[chart_name] = [];
      }
      acc[chart_name] = item;
      return acc;
    }, {});

    this.setState({ lineChartThresholds: groupByChartName });
  };

  componentDidUpdate(prevProps) {
    const tenant =
      this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const prevTenant =
      prevProps?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const isTenantChanged = tenant !== prevTenant;
    if (isTenantChanged) {
      this.setState({ tenant, lineChartThresholds: {} }, () => {
        this.getLineChartThresholds();
      });
    }
  }

  datePickerOnChanged = e => {
    const BOUNCE_IN_MILISECONDS = 300;
    if (e.value) {
      const debouncedFunc = debounce(() => {
        this.setState({ selectedDate: e.value });
      }, BOUNCE_IN_MILISECONDS);
      if (this.pendingRequest) {
        // Cancel any pending requests
        clearTimeout(this.pendingRequest);
      }
      // Schedule the debounced function to run after 500ms
      this.pendingRequest = setTimeout(() => {
        debouncedFunc();
        this.pendingRequest = null;
      }, BOUNCE_IN_MILISECONDS);
    }
  };

  sortDropDownOptionsOnChange = debounce(e => {
    this.setState({ selectedSort: e.value }, () => {
      // Do something here
    });
  }, 300);

  renderSortDropdown() {
    const { selectedSort } = this.state;
    return (
        <CustomDropdown
          label="ソート順"
          value={selectedSort}
          options={this.sortDropDownOptions}
          onChange={this.sortDropDownOptionsOnChange}
        style={{ width: '220px' }}
        className="performance-filter-dropdown"
        rounded={true}
        ></CustomDropdown>
    );
  }

  renderDatePicker() {
    const { selectedDate } = this.state;
    const maxDate = new Date();
    return (
      <CustomCalendar
        label="日付"
        value={selectedDate}
        onChange={this.datePickerOnChanged}
        maxDate={maxDate}
        className="performance-filter-calendar"
        style={{ width: '130px' }}
      ></CustomCalendar>
    );
  }

  // Children Component Callback
  updateDeviceInfoOrderList({ deviceInfoOrderedList: newValue, isLoadMore }) {
    let newDeviceIdList = [];
    let isReset = false;
    if (
      !this.loadedDeviceIdList ||
      this.loadedDeviceIdList.length === 0 ||
      !isLoadMore
    ) {
      this.loadedDeviceIdList = [];
      isReset = true;
    }

    // filter out the device id that is not in the loaded list
    newValue.forEach(item => {
      if (!this.loadedDeviceIdList.includes(item.deviceId)) {
        newDeviceIdList.push(item.deviceId);
        this.loadedDeviceIdList.push(item.deviceId);
      }
    });

    let lineOptions = newValue
      .map(item => {
        const { deviceName, deviceId } = item;
        return {
          name: deviceName,
          code: deviceId,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    const selectedDeviceIdLineList = lineOptions.map(item => item.code);
    this.setState({
      selectedLineList: [...lineOptions],
      lineOptions,
      addedDeviceIdList: [...newDeviceIdList],
      isResetDeviceIdList: isReset,
      deviceInfoOrderedList: newValue,
      selectedDeviceIdLineList,
    });
  }

  limitDropDownOptionsOnChange = debounce(e => {
    this.setState({ selectedLimit: e.value }, () => {});
  }, 300);

  renderLimitDropdown() {
    const { selectedLimit } = this.state;
    return (
        <CustomDropdown
          label="選択限度"
          value={selectedLimit}
          options={this.limitDropDownOptions}
          onChange={this.limitDropDownOptionsOnChange}
          style={{ width: '130px' }}
        className="performance-filter-dropdown"
        rounded={true}
        ></CustomDropdown>
    );
  }

  multiselectLineChartOnChanged = e => {
    const selectedDeviceIdLineList = e.value.map(item => item.code);
    this.setState({ selectedLineList: e.value, selectedDeviceIdLineList });
  };

  renderMultiSelectLineChart = () => {
    const { selectedLineList, lineOptions } = this.state;

    const itemTemplate = option => {
      return (
        <div className="multi-select-item-template" style={{backgroundColor: option.fillColor}}>
          {option.name}
        </div>
      );
    };
    
    const footerTemplate = () => {
      const length = selectedLineList ? selectedLineList.length : 0;

      return (
        <div className="py-2 px-3">
          <span>
            <b>{length}</b> アイテムが選択されました
          </span>
        </div>
      );
    };
    return (
      <MultiSelect
        value={selectedLineList}
        options={lineOptions}
        disabled={lineOptions.length === 0}
        onChange={this.multiselectLineChartOnChanged}
        optionLabel="name"
        placeholder={COMMON_TEXT.SELECT}
        itemTemplate={itemTemplate}
        panelFooterTemplate={footerTemplate}
        className="multiselect-style"
        display="chip"
        filter
      />
    );
  };

  renderRoomDropdown() {
    const { selectedLineList, selectedDeviceIdLineList } = this.state;
    const isDisabled = !selectedLineList || selectedLineList.length === 0;
    
    // Map selectedDeviceIdLineList back to the actual selected objects
    const selectedItems = selectedLineList.filter(item => 
      selectedDeviceIdLineList.includes(item.code)
    );

    return (
      <CustomMultiSelect
        label="アクセスポイント"
        value={selectedItems}
        options={selectedLineList}
        optionLabel="name"
        onChange={this.handleRoomDropdownChange}
        disabled={isDisabled}
        placeholder="選択してください"
        className="performance-filter-multiselect"
        style={{ width: '200px' }}
      />
    );
  }

  handleRoomDropdownChange = (e) => {
    const selectedItems = e.value || [];
    const selectedDeviceIdLineList = selectedItems.map(item => item.code);
    this.setState({ selectedDeviceIdLineList });
  };

  renderRefreshButton() {
    const { tenant } = this.state;
    return (
      <button 
        className="refresh-button"
        disabled={!tenant}
        onClick={() => {
          this.getLineChartThresholds();
        }}
      >
        <i className="pi pi-refresh" />
      </button>
    );
  }

  tabButtonOnChanged = (tabIndex) => {
    this.setState({ activeTab: tabIndex });
  };

  renderTabNavigation = () => {
    const { activeTab } = this.state;
    
    return (
      <div className="network-header">
        <div className="tab-menu">
          <button 
            className={`tab-btn${activeTab === 0 ? ' selected' : ''}`}
            onClick={() => this.tabButtonOnChanged(0)}
            type="button"
          >
            パフォーマンススコア・信号品質
          </button>
          <button 
            className={`tab-btn${activeTab === 1 ? ' selected' : ''}`}
            onClick={() => this.tabButtonOnChanged(1)}
            type="button"
          >
            接続端末数・データレート・チャンネル利用率
          </button>
          <button 
            className={`tab-btn${activeTab === 2 ? ' selected' : ''}`}
            onClick={() => this.tabButtonOnChanged(2)}
            type="button"
          >
            遅延
          </button>
        </div>
      </div>
    );
  };

  renderTabContent() {
    const {
      activeTab,
      selectedDate,
      selectedSort,
      selectedLimit,
      selectedDeviceIdLineList,
      deviceInfoOrderedList,
      addedDeviceIdList,
      isResetDeviceIdList,
      lineChartThresholds,
    } = this.state;

    return (
      <>
        {/* Always render NetworkPerformanceScore to maintain data flow, but hide when not active */}
        <div style={{ display: activeTab === 0 ? 'block' : 'none', width: '100%' }}>
          <NetworkPerformanceScore
            selectedDate={selectedDate}
            selectedLimit={selectedLimit}
            updateDeviceInfoOrderList={this.updateDeviceInfoOrderList}
            selectedDeviceIdLineList={selectedDeviceIdLineList}
            sortOption={selectedSort}
          />
          <WirelessSignal
            selectedDate={selectedDate}
            selectedDeviceIdLineList={selectedDeviceIdLineList}
            deviceInfoOrderedList={deviceInfoOrderedList}
            addedDeviceIdList={addedDeviceIdList}
            isResetDeviceIdList={isResetDeviceIdList}
          />
        </div>

        {/* Tab 2: Connection count + Data rate + Channel utilization */}
        {activeTab === 1 && (
          <div className="tab2-layout">
            {/* Top row: Connection count + Data rate side by side */}
            <div className="tab2-top-row">
              <div className="tab2-chart-container">
                <NetworkClientCount
                  selectedDate={selectedDate}
                  selectedDeviceIdLineList={selectedDeviceIdLineList}
                  deviceInfoOrderedList={deviceInfoOrderedList}
                  addedDeviceIdList={addedDeviceIdList}
                  isResetDeviceIdList={isResetDeviceIdList}
                  lineChartThreshold={lineChartThresholds}
                />
              </div>
              <div className="tab2-chart-container">
                <NetworkUsage
                  selectedDate={selectedDate}
                  selectedDeviceIdLineList={selectedDeviceIdLineList}
                  deviceInfoOrderedList={deviceInfoOrderedList}
                  addedDeviceIdList={addedDeviceIdList}
                  isResetDeviceIdList={isResetDeviceIdList}
                  lineChartThreshold={lineChartThresholds}
                />
              </div>
            </div>
            
            {/* Bottom row: Channel utilization - full width */}
            <div className="tab2-bottom-row">
              <WirelessChannelUtilization
                selectedDate={selectedDate}
                selectedDeviceIdLineList={selectedDeviceIdLineList}
                deviceInfoOrderedList={deviceInfoOrderedList}
                addedDeviceIdList={addedDeviceIdList}
                isResetDeviceIdList={isResetDeviceIdList}
                lineChartThreshold={lineChartThresholds}
              />
            </div>
          </div>
        )}

        {/* Tab 3: Latency */}
        {activeTab === 2 && (
          <WirelessLatency
            selectedDate={selectedDate}
            selectedDeviceIdLineList={selectedDeviceIdLineList}
            deviceInfoOrderedList={deviceInfoOrderedList}
            addedDeviceIdList={addedDeviceIdList}
            isResetDeviceIdList={isResetDeviceIdList}
            lineChartThreshold={lineChartThresholds}
          />
        )}
      </>
    );
  }

  render() {
    const { tenant } = this.state;

    if (!tenant) {
      return (
        <div className="config-container custom-config-container network-usage">
          <div className="p-message p-message-error" style={{ margin: '20px 60px' }}>
            <div className="p-message-text">{COMMON_TEXT.TENANT_NOT_SELECTED_PLEASE_SELECT}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="config-container custom-config-container network-usage">
        {/* Top Filter Controls Row */}
        <div className="config-title-container performance-config-title-container">
          <div className="performance-config-container">
            <div className="upper" style={{ 
              display: 'flex', 
              gap: '20px', 
              alignItems: 'flex-end',
              marginBottom: '20px',
              justifyContent: 'flex-start'
            }}>
              <div className="filter-control">{this.renderLimitDropdown()}</div>
              <div className="filter-control">{this.renderSortDropdown()}</div>
              <div className="filter-control">{this.renderDatePicker()}</div>
              <div className="filter-control">{this.renderRoomDropdown()}</div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        {this.renderTabNavigation()}

        {/* Main Content */}
        <div className="main-content">
          <div className="config-content">
            {this.renderTabContent()}
          <ScrollTop
            target="parent"
            threshold={400}
            className=""
            style={{
              height: 40,
              width: 40,
              maxHeight: 40,
              minHeight: 40,
              maxWidth: 40,
              minWidth: 40,
            }}
            icon="pi pi-arrow-up text-base"
          />
          </div>
        </div>
      </div>
    );
  }
}

Network.propTypes = {};

export default Network;
